<template>
  <div class="settings-page">
    <div class="container">

      <!-- 新增：登录后页面配置卡片 -->
      <div class="setting-card">
        <div class="card-header">
          <h2><i class="fas fa-sign-in-alt"></i> {{ $t('system.loginRedirectPage') }}</h2>
          <div class="card-actions">
            <el-button type="info" size="small" @click="showSysLogDialog">
              <i class="fas fa-history"></i> {{ $t('system.systemLog') }}
            </el-button>
            <el-button type="primary" size="small" @click="openLoginPageDialog">
              <i class="fas fa-edit"></i> {{ $t('system.edit') }}
            </el-button>
          </div>
        </div>
        <div class="card-content">
          <el-descriptions :column="1" border>
            <el-descriptions-item :label="$t('system.loginRedirectPage')">
              {{ getPageName(loginPageSettings.loginPage) }}
            </el-descriptions-item>
          </el-descriptions>
        </div>
      </div>

      <!-- Web设置卡片 -->
      <div class="setting-card" v-if="loadCompo?.includes('filemgmt')">
        <div class="card-header">
          <h2><i class="fas fa-cog"></i> {{ $t('system.webSettings') }}</h2>
            <el-button type="primary" size="small" @click="openWebDialog">
              <i class="fas fa-edit"></i> {{ $t('system.edit') }}
            </el-button>
        </div>
        <div class="card-content">
          <el-descriptions :column="2" border>
            <el-descriptions-item :label="$t('system.httpPort')">{{ webSettings.httpPort }}</el-descriptions-item>
            <el-descriptions-item :label="$t('system.httpStatus')">{{ webSettings.httpStatus === 'on' ? $t('system.on') : $t('system.off') }}</el-descriptions-item>
            <el-descriptions-item :label="$t('system.httpsPort')">{{ webSettings.httpsPort }}</el-descriptions-item>
            <el-descriptions-item :label="$t('system.httpsStatus')">{{ webSettings.httpsStatus === 'on' ? $t('system.on') : $t('system.off') }}</el-descriptions-item>
            <el-descriptions-item :label="$t('system.certPath')" :span="2">{{ webSettings.defineCertPath || '-' }}</el-descriptions-item>
            <el-descriptions-item :label="$t('system.webdavSuffix')" :span="2">{{ webSettings.webdavIndex || '-' }}</el-descriptions-item>
          </el-descriptions>
        </div>
      </div>

      <!-- 免密登录设置卡片 -->
      <div class="setting-card">
        <div class="card-header">
          <h2><i class="fas fa-user-shield"></i> {{ $t('system.noPasswordLogin') }}</h2>
            <el-button type="primary" size="small" @click="openAuthDialog">
              <i class="fas fa-edit"></i> {{ $t('system.edit') }}
            </el-button>
        </div>
        <div class="card-content">
          <el-descriptions :column="1" border>
            <el-descriptions-item :label="$t('system.currentStatus')">
              <el-tag :type="authSettings.status === $t('system.on') ? 'success' : 'info'">
                {{ authSettings.status }}
              </el-tag>
            </el-descriptions-item>
          </el-descriptions>
        </div>
      </div>

      <!-- 日志设置卡片 -->
      <div class="setting-card">
        <div class="card-header">
          <h2><i class="fas fa-clipboard-list"></i> {{ $t('system.logSettings') }}</h2>
            <el-button type="primary" size="small" @click="openLogDialog">
              <i class="fas fa-edit"></i> {{ $t('system.edit') }}
            </el-button>
        </div>
        <div class="card-content">
          <el-descriptions :column="2" border>
            <el-descriptions-item :label="$t('system.logLevel')">{{ logSettings.level }}</el-descriptions-item>
            <el-descriptions-item :label="$t('system.logDays')">{{ logSettings.day }}{{ $t('common.day') }}</el-descriptions-item>
          </el-descriptions>
        </div>
      </div>

      <!-- 缓存文件设置卡片 -->
      <div class="setting-card" v-if="loadCompo?.includes('filemgmt')">
        <div class="card-header">
          <h2><i class="fas fa-hdd"></i> {{ $t('system.cacheSettings') }}</h2>
            <el-button type="primary" size="small" @click="openCacheDialog">
              <i class="fas fa-edit"></i> {{ $t('system.edit') }}
            </el-button>
        </div>
        <div class="card-content">
          <el-descriptions :column="2" border>
            <el-descriptions-item :label="$t('system.cacheAddress')" :span="2">{{ cacheSettings.path }}</el-descriptions-item>
            <el-descriptions-item :label="$t('system.cacheTime')">{{ cacheSettings.time }}{{ $t('common.second') }}</el-descriptions-item>
            <el-descriptions-item :label="$t('system.deleteToRecycle')">{{ cacheSettings.recycle === 'on' ? $t('system.on') : $t('system.off') }}</el-descriptions-item>
          </el-descriptions>
        </div>
      </div>

      <!-- 安全入口路径卡片 -->
      <div class="setting-card">
        <div class="card-header">
          <h2><i class="fas fa-lock"></i>{{ $t('system.securityPath') }}</h2>
            <el-button type="primary" size="small" @click="openSecurityDialog">
              <i class="fas fa-edit"></i> {{ $t('system.edit') }}
            </el-button>
        </div>
        <div class="card-content">
          <el-descriptions :column="1" border>
            <el-descriptions-item :label="$t('system.path')">{{ securitySettings.path || '-' }}</el-descriptions-item>
          </el-descriptions>
        </div>
      </div>

      <!-- 备份配置卡片 -->
      <div class="setting-card">
        <div class="card-header">
          <h2><i class="fas fa-file-export"></i> {{ $t('system.backupConfig') }}</h2>
        </div>
        <div class="card-content">
          <div class="backup-actions">
            <el-button type="primary" @click="backupAllConfig" :loading="backupLoading">
              {{ $t('system.backupAllConfig') }}
            </el-button>
            <el-button type="primary" @click="backupCfgConfig" :loading="backupLoading">
              {{ $t('system.backupCfgConfig') }}
            </el-button>
          </div>
        </div>
      </div>

      <!-- 其他操作卡片 -->
      <div class="setting-card">
        <div class="card-header">
          <h2><i class="fas fa-ellipsis-h"></i> {{ $t('system.otherOperations') }}</h2>
        </div>
        <div class="card-content">
          <el-button type="primary" @click="restartSoftware" :loading="restartLoading">
            {{ $t('system.restartSoftware') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 登录后页面设置对话框 -->
    <el-dialog v-model="loginPageDialogVisible" :title="$t('system.editLoginRedirectPage')" :width="isMobile ? '100%' : '600px'" append-to-body>
      <el-form :model="loginPageForm" label-width="120px">
        <el-form-item :label="$t('system.loginRedirectPage')">
          <el-select v-model="loginPageForm.loginPage" style="width: 100%">
            <el-option :label="$t('system.homepage')" value="sys-home" />
            <el-option :label="$t('system.userConfig')" value="user-mgmt" />
            <el-option :label="$t('nav.fileSetting')" value="file-setting" />
            <el-option :label="$t('system.fileList')" value="file-list" />
            <el-option :label="$t('nav.collectList')" value="collect-list" />
            <el-option :label="$t('nav.shareLinkList')" value="shareLink-list" />
            <el-option :label="$t('nav.fileSyncMgmt')" value="file-sync-mgmt" />
            <el-option :label="$t('nav.transferServer')" value="transfer-server" />
            <el-option :label="$t('nav.distributionServer')" value="distribution-server" />
            <el-option :label="$t('nav.officialClient')" value="official-client" />
            <el-option :label="$t('nav.serverMapping')" value="server-mapping" />
            <el-option :label="$t('nav.p2pSetting')" value="p2p-setting" />
            <el-option :label="$t('nav.p2pClient')" value="p2p-client" />
            <el-option :label="$t('nav.proxyMgmt')" value="proxy-mgmt" />
            <el-option :label="$t('nav.reverseProxy')" value="reverse-proxy" />
            <el-option :label="$t('nav.aliyunDdns')" value="aliyun-ddns" />
            <el-option :label="$t('nav.pubyunDdns')" value="pubyun-ddns" />
            <el-option :label="$t('nav.nameyunDdns')" value="nameyun-ddns" />
            <el-option :label="$t('nav.tencentcloudDdns')" value="tencentcloud-ddns" />
            <el-option :label="$t('nav.cloudflareDdns')" value="cloudflare-ddns" />
            <el-option :label="$t('nav.mountMgmt')" value="mount-mgmt" />
            <el-option :label="$t('nav.deviceCenter')" value="device-center" />
            <el-option :label="$t('nav.sysSetting')" value="sys-setting" />
            <el-option :label="$t('nav.sysAbout')" value="sys-about" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="loginPageDialogVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="updateLoginPage" :loading="loginPageLoading">{{ $t('common.confirm') }}</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- Web设置对话框 -->
    <el-dialog v-model="webDialogVisible" :title="$t('system.editWebSettings')" :width="isMobile ? '100%' : '600px'" append-to-body>
      <el-form :model="webForm" label-width="120px">
        <el-form-item :label="$t('system.httpPort')">
          <el-input v-model="webForm.httpPort" />
        </el-form-item>
        <el-form-item :label="$t('system.httpStatus')">
          <el-select v-model="webForm.httpStatus">
            <el-option :label="$t('system.on')" value="on" />
            <el-option :label="$t('system.off')" value="off" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('system.httpsPort')">
          <el-input v-model="webForm.httpsPort" />
        </el-form-item>
        <el-form-item :label="$t('system.httpsStatus')">
          <el-select v-model="webForm.httpsStatus">
            <el-option :label="$t('system.on')" value="on" />
            <el-option :label="$t('system.off')" value="off" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('system.certPath')">
          <div class="path-input-group">
            <el-input v-model="webForm.defineCertPath" :placeholder="$t('common.required')" />
            <el-button @click="openFolderSelector('cert')" type="primary" size="default">{{ $t('system.browse') }}</el-button>
          </div>
        </el-form-item>
        <el-form-item :label="$t('system.webdavSuffix')">
          <el-input v-model="webForm.webdavIndex" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="webDialogVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="updateWebConfig" :loading="webLoading">{{ $t('common.confirm') }}</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 免密登录设置对话框 -->
    <el-dialog v-model="authDialogVisible" :title="$t('system.editNoPasswordLogin')" :width="isMobile ? '100%' : '400px'" append-to-body>
      <el-form :model="authForm" label-width="120px">
        <el-form-item :label="$t('system.noPasswordLoginStatus')">
          <el-switch
            v-model="authForm.status"
            active-value="已开启"
            inactive-value="已关闭"
            :active-text="$t('system.on')"
            :inactive-text="$t('system.off')"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="authDialogVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="toggleAuthLogin" :loading="authLoading">{{ $t('common.confirm') }}</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 日志设置对话框 -->
    <el-dialog v-model="logDialogVisible" :title="$t('system.editLogSettings')" :width="isMobile ? '100%' : '500px'" append-to-body>
      <el-form :model="logForm" label-width="140px">
        <el-form-item :label="$t('system.logLevel')">
          <el-select v-model="logForm.level" style="width: 100%">
            <el-option label="debug" value="debug" />
            <el-option label="info" value="info" />
            <el-option label="warn" value="warn" />
            <el-option label="error" value="error" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('system.logDays') + '(0-30)'">
          <el-input-number v-model="logForm.day" :min="0" :max="30" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="updateLogConfig" :loading="logLoading">{{ $t('common.confirm') }}</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 缓存设置对话框 -->
    <el-dialog v-model="cacheDialogVisible" :title="$t('system.editCacheSettings')" :width="isMobile ? '100%' : '600px'" append-to-body>
      <el-form :model="cacheForm" label-width="140px">
        <el-form-item :label="$t('system.cacheAddress')">
          <div class="path-input-group">
            <el-input v-model="cacheForm.path" :placeholder="$t('common.required')" />
            <el-button @click="openFolderSelector('cache')" type="primary" size="default">{{ $t('system.browse') }}</el-button>
          </div>
        </el-form-item>
        <el-form-item :label="$t('system.cacheTime') + '(' + $t('common.second') + ')'">
          <el-input v-model="cacheForm.time" />
        </el-form-item>
        <el-form-item :label="$t('system.deleteToRecycle')">
          <el-select v-model="cacheForm.recycle">
            <el-option :label="$t('system.on')" value="on" />
            <el-option :label="$t('system.off')" value="off" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="cacheDialogVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="updateCacheSettings" :loading="cacheLoading">{{ $t('common.confirm') }}</el-button>
          <el-button @click="resetCacheSettings" :loading="cacheLoading">{{ $t('common.reset') }}</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 安全路径设置对话框 -->
    <el-dialog v-model="securityDialogVisible" :title="$t('system.editSecurityPath')" :width="isMobile ? '100%' : '500px'" append-to-body>
      <el-form :model="securityForm" label-width="120px">
        <el-form-item :label="$t('system.path')">
          <el-input v-model="securityForm.path" :placeholder="$t('common.required')" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="securityDialogVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="updateSecurityPath" :loading="securityLoading">{{ $t('common.confirm') }}</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 系统日志查看对话框 -->
    <el-dialog
      v-model="sysLogDialogVisible"
      :title="$t('log.title')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="log-dialog"
      append-to-body
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('log.logTotal', { count: sysLogTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('log.autoRefresh') }}</span>
            <el-switch
              v-model="sysAutoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleSysAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="sysAutoRefreshEnabled">(5{{ $t('log.second') }})</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllSysLogs"
            :loading="sysLogDownloading"
            :icon="Download"
          >
            {{ $t('log.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshSysLogs"
            :loading="sysLogLoading"
            :icon="Refresh"
          >
            {{ $t('common.refresh') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearSysLogs"
            :loading="sysLogClearing"
          >
            {{ $t('log.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="sysLogContentRef">
        <div v-if="sysLogsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('log.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div 
            v-for="(log, index) in sysLogsData" 
            :key="index" 
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="sysLogTotal > 0">
        <el-pagination
          v-model:current-page="sysLogCurrentPage"
          v-model:page-size="sysLogPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="sysLogTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSysLogSizeChange"
          @current-change="handleSysLogPageChange"
          small
        />
      </div>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="sysLogDialogVisible = false">{{ $t('common.close') }}</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 文件夹选择对话框 - 使用独立于el-dialog的div，确保z-index最高 -->
    <div v-if="showFolderModal" class="folder-modal-overlay" @click.self="closeFolderModal">
      <div class="folder-modal-container">
        <div class="folder-modal">
          <div class="folder-modal-header">
            <button
              v-if="currentFolderPath !== '/'"
              @click="goToParentFolder"
              class="nav-btn"
            >
              ↑ {{ $t('file.folderModal.parentFolder') }}
            </button>
            <h3>{{ $t('file.folderModal.currentPath') }}: {{ currentFolderPath || '/' }}</h3>
            <button @click="closeFolderModal" class="close-btn">✕</button>
          </div>

          <!-- 手动输入区域 - 直接使用输入框，不需要额外确认按钮 -->
          <div class="manual-input-section">
            <div class="input-label">{{ $t('file.folderModal.manualInputPath') }}:</div>
            <div class="input-controls">
              <input
                type="text"
                v-model="manualDirectoryPath"
                :placeholder="$t('file.folderModal.enterPathHint')"
                class="directory-input"
                @input="onManualPathInput"
                @keyup.enter="useManualPath"
              />
              <button @click="useManualPath" class="use-path-btn">{{ $t('file.folderModal.useThisPath') }}</button>
            </div>
            <div class="input-hint">{{ $t('file.folderModal.afterInputHint') }}</div>
          </div>

          <div class="folder-modal-content">
            <div v-if="folders.length > 0" class="directory-list">
              <div
                v-for="folder in folders"
                :key="folder.Path"
                @click="selectFolder(folder)"
                @dblclick="enterFolder(folder)"
                class="directory-item"
                :class="{ 'selected': selectedFolder && selectedFolder.Path === folder.Path }"
              >
                <span class="folder-icon">📁</span>
                <span class="directory-name">{{ folder.Name }}</span>
                <button @click.stop="enterFolder(folder)" class="enter-btn">→</button>
              </div>
            </div>
            <div v-else class="empty-directory">
              📁 {{ $t('file.folderModal.emptyDirectory') }}
            </div>
          </div>

          <div class="folder-modal-footer">
            <div class="selected-path" v-if="selectedFolder || manualDirectoryPath">
              {{ $t('file.folderModal.selected') }}: {{ selectedFolder ? selectedFolder.Path : manualDirectoryPath }}
            </div>
            <div class="action-buttons">
              <button @click="closeFolderModal" class="cancel-btn">{{ $t('common.cancel') }}</button>
              <button
                @click="confirmFolderSelection"
                class="confirm-btn"
                :disabled="!selectedFolder && !manualDirectoryPath"
              >
                {{ $t('common.confirm') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Refresh, Download, InfoFilled } from '@element-plus/icons-vue';
import { API_BASE_URL } from '@/config';
import { HOME_BASE_URL } from '@/config';

export default {
  name: 'SettingsPage',
  components: {
    InfoFilled
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter();
    
    // 加载状态
    const loading = ref(true);
    const webLoading = ref(false);
    const authLoading = ref(false);
    const cacheLoading = ref(false);
    const securityLoading = ref(false);
    const backupLoading = ref(false);
    const uploadLoading = ref(false);
    const restartLoading = ref(false);
    const loadCompo = ref('');

    // 对话框显示状态
    const loginPageDialogVisible = ref(false);
    const webDialogVisible = ref(false);
    const authDialogVisible = ref(false);
    const logDialogVisible = ref(false);
    const cacheDialogVisible = ref(false);
    const securityDialogVisible = ref(false);

    // 系统日志对话框相关
    const sysLogDialogVisible = ref(false);
    const sysLogLoading = ref(false);
    const sysLogClearing = ref(false);
    const sysLogDownloading = ref(false);
    const sysLogsData = ref([]);
    const sysLogTotal = ref(0);
    const sysLogCurrentPage = ref(1);
    const sysLogPageSize = ref(50);
    const sysLogContentRef = ref(null);
    const sysAutoRefreshEnabled = ref(true);
    const sysLogRefreshInterval = ref(null);

    // 文件夹选择相关状态
    const showFolderModal = ref(false);
    const currentFolderPath = ref('/');
    const folders = ref([]);
    const selectedFolder = ref(null);
    const manualDirectoryPath = ref('');
    const currentPathTarget = ref(''); // 'cert' 或 'cache'
    const isUsingManualPath = ref(false); // 标记是否正在使用手动输入
    const currentPath = ref('');

    const isMobile = window.innerWidth < 768;

    // 表单数据（用于编辑）
    const loginPageForm = ref({
      loginPage: ''
    });
    const webForm = ref({
      httpPort: '',
      httpStatus: '',
      httpsPort: '',
      httpsStatus: '',
      defineCertPath: '',
      webdavIndex: ''
    });
    const authForm = ref({
      status: ''
    });
    const logForm = ref({
      level: '',
      day: 0,
      userLogDay: 30
    });
    const cacheForm = ref({
      path: '',
      time: '',
      recycle: ''
    });
    const securityForm = ref({
      path: ''
    });

    // 登录后页面配置相关状态
    const loginPageSettings = ref({
      loginPage: 'sys-home'
    });
    const loginPageLoading = ref(false);

    // Web设置
    const webSettings = ref({
      httpPort: '8866',
      httpStatus: 'on',
      httpsPort: '8886',
      httpsStatus: 'off',
      defineCertPath: '',
      webdavIndex: ''
    });

    // 免密登录设置
    const authSettings = ref({
      status: '已关闭'
    });

    // 缓存文件设置
    const cacheSettings = ref({
      path: '/system/bin/tmpStorage',
      time: '43200',
      recycle: 'off'
    });

    // 安全入口路径
    const securitySettings = ref({
      path: ''
    });

    // 日志设置相关状态
    const logSettings = ref({
      level: 'debug',
      day: 0,
      userLogDay: 30,
      output: 'tfcenter-service.txt'
    });

    const logLoading = ref(false);

    // 自动刷新定时器
    let refreshTimer = null;

    // 检查登录状态
    const checkSysStatus = () => {
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        try {
          const user = JSON.parse(userInfo);
          loadCompo.value = user.loadCompo || '';
        } catch (e) {
          console.error('解析用户信息失败', e);
        }
      }
    };

    // 获取页面名称
    const getPageName = (value) => {
      const pageMap = {
        'sys-home': t('system.homepage'),
        'user-mgmt': t('system.userConfig'),
        'file-setting': t('file.fileSetting'),
        'file-list': t('system.fileList'),
        'collect-list': t('nav.collectList'),
        'shareLink-list': t('nav.shareFolder'),
        'file-sync-mgmt': t('nav.fileSync'),
        'transfer-server': t('nav.transferServer'),
        'distribution-server': t('nav.distributionServer'),
        'official-client': t('nav.officialClient'),
        'server-mapping': t('nav.serverMapping'),
        'p2p-setting': t('nav.p2pSetting'),
        'p2p-client': t('nav.p2pClient'),
        'proxy-mgmt': t('nav.proxyMgmt'),
        'reverse-proxy': t('nav.reverseProxy'),
        'aliyun-ddns': t('nav.aliyunDdns'),
        'pubyun-ddns': t('nav.pubyunDdns'),
        'nameyun-ddns': t('nav.namecomDdns'),
        'tencentcloud-ddns': t('nav.tencentcloudDdns'),
        'cloudflare-ddns': t('nav.cloudflareDdns'),
        'acme-mgmt': t('nav.acmeManagement'),
        'mount-mgmt': t('nav.mountMgmt'),
        'device-center': t('nav.deviceCenter'),
        'sys-setting': t('nav.sysSetting'),
        'sys-about': t('nav.sysAbout')
      };
      return pageMap[value] || value;
    };

    // 打开对话框并填充当前数据
    const openLoginPageDialog = () => {
      loginPageForm.value.loginPage = loginPageSettings.value.loginPage;
      loginPageDialogVisible.value = true;
    };

    const openWebDialog = () => {
      webForm.value = { ...webSettings.value };
      webDialogVisible.value = true;
    };

    const openAuthDialog = () => {
      authForm.value.status = authSettings.value.status;
      authDialogVisible.value = true;
    };

    const openLogDialog = () => {
      logForm.value = {
        level: logSettings.value.level,
        day: logSettings.value.day,
        userLogDay: logSettings.value.userLogDay
      };
      logDialogVisible.value = true;
    };

    const openCacheDialog = () => {
      cacheForm.value = { ...cacheSettings.value };
      cacheDialogVisible.value = true;
    };

    const openSecurityDialog = () => {
      securityForm.value.path = securitySettings.value.path;
      securityDialogVisible.value = true;
    };

    // 打开系统日志对话框
    const showSysLogDialog = () => {
      sysLogDialogVisible.value = true;
      fetchSysLogs();
      if (sysAutoRefreshEnabled.value) {
        startSysLogAutoRefresh();
      }
    };

    // 切换系统日志自动刷新
    const toggleSysAutoRefresh = (enabled) => {
      if (enabled) {
        startSysLogAutoRefresh();
        ElMessage.success('已开启自动刷新（5秒/次）');
      } else {
        stopSysLogAutoRefresh();
        ElMessage.info('已关闭自动刷新');
      }
    };

    // 启动系统日志自动刷新
    const startSysLogAutoRefresh = () => {
      stopSysLogAutoRefresh();
      sysLogRefreshInterval.value = setInterval(() => {
        if (sysLogDialogVisible.value) {
          fetchSysLogs(false);
        }
      }, 5000);
    };

    // 停止系统日志自动刷新
    const stopSysLogAutoRefresh = () => {
      if (sysLogRefreshInterval.value) {
        clearInterval(sysLogRefreshInterval.value);
        sysLogRefreshInterval.value = null;
      }
    };

    // 获取系统日志
    const fetchSysLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          sysLogLoading.value = true;
        }
        
        const params = new URLSearchParams({
          module: 'settings',
          page: sysLogCurrentPage.value,
          size: sysLogPageSize.value
        });
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error('网络请求失败');
        
        const data = await response.json();
        
        if (data.errCode === 1000) {
          ElMessage.error('请先登录系统');
          router.push('/user-login?callback=sys-setting');
          return;
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '获取日志失败');
        }
        
        sysLogsData.value = data.data || [];
        sysLogTotal.value = data.count || 0;
        
        if (showLoading && sysLogContentRef.value) {
          sysLogContentRef.value.scrollTop = 0;
        }
      } catch (error) {
        console.error('获取系统日志失败:', error);
        if (showLoading) {
          ElMessage.error(`获取日志失败: ${error.message || '未知错误'}`);
        }
      } finally {
        if (showLoading) {
          sysLogLoading.value = false;
        }
      }
    };

    // 刷新系统日志
    const refreshSysLogs = () => {
      fetchSysLogs(true);
    };

    // 清空系统日志
    const clearSysLogs = async () => {
      try {
        await ElMessageBox.confirm('确定要清空所有系统日志吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        
        sysLogClearing.value = true;
        
        const params = new URLSearchParams({
          module: 'settings'
        });
        
        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error('网络请求失败');
        
        const data = await response.json();
        
        if (data.errCode === 1000) {
          ElMessage.error('请先登录系统');
          router.push('/user-login?callback=sys-setting');
          return;
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '清空日志失败');
        }
        
        ElMessage.success('系统日志已清空');
        fetchSysLogs(true);
      } catch (error) {
        if (error !== 'cancel') {
          console.error('清空系统日志失败:', error);
          ElMessage.error(`清空日志失败: ${error.message || '未知错误'}`);
        }
      } finally {
        sysLogClearing.value = false;
      }
    };

    // 下载所有系统日志
    const downloadAllSysLogs = async () => {
      try {
        sysLogDownloading.value = true;
        
        const params = new URLSearchParams({
          module: 'settings',
          page: 1,
          size: 10000
        });
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error('网络请求失败');
        
        const data = await response.json();
        
        if (data.errCode === 1000) {
          ElMessage.error('请先登录系统');
          router.push('/user-login?callback=sys-setting');
          return;
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '获取日志失败');
        }
        
        const allLogs = data.data || [];
        
        if (allLogs.length === 0) {
          ElMessage.warning('暂无日志可下载');
          return;
        }
        
        let logContent = '';
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`;
        });
        
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `system_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        ElMessage.success(`所有日志下载成功，共 ${allLogs.length} 条记录`);
      } catch (error) {
        console.error('下载系统日志失败:', error);
        ElMessage.error(`下载日志失败: ${error.message || '未知错误'}`);
      } finally {
        sysLogDownloading.value = false;
      }
    };

    // 处理系统日志每页条数变化
    const handleSysLogSizeChange = (size) => {
      sysLogPageSize.value = size;
      sysLogCurrentPage.value = 1;
      fetchSysLogs(true);
    };

    // 处理系统日志页码变化
    const handleSysLogPageChange = (page) => {
      sysLogCurrentPage.value = page;
      fetchSysLogs(true);
    };

    // 打开文件夹选择器
    const openFolderSelector = (target) => {
      console.log('打开文件夹选择器，目标:', target);
      currentPathTarget.value = target;
      showFolderModal.value = true;
      currentFolderPath.value = currentPath.value;
      manualDirectoryPath.value = '';
      selectedFolder.value = null;
      isUsingManualPath.value = false;
      fetchFolders(currentFolderPath.value);
    };

    // 获取文件夹列表
    const fetchFolders = async (path) => {
      try {
        console.log('获取文件夹列表，路径:', path);
        const response = await fetch(`${API_BASE_URL}/get-dirList?path=${encodeURIComponent(path)}`, {
          credentials: 'include'
        });
        const data = await response.json();
        folders.value = data.file || [];
        console.log('获取到的文件夹:', folders.value);
      } catch (error) {
        console.error('获取文件夹列表失败:', error);
        folders.value = [];
      }
    };

    // 选择文件夹
    const selectFolder = (folder) => {
      console.log('选择文件夹:', folder);
      selectedFolder.value = folder;
      isUsingManualPath.value = false; // 取消手动输入模式
    };

    // 进入文件夹
    const enterFolder = (folder) => {
      console.log('进入文件夹:', folder);
      currentFolderPath.value = folder.Path;
      fetchFolders(currentFolderPath.value);
    };

    // 返回上一级
    const goToParentFolder = () => {
      const parts = currentFolderPath.value.split('/');
      parts.pop();
      currentFolderPath.value = parts.join('/') || '/';
      console.log('返回上一级，新路径:', currentFolderPath.value);
      fetchFolders(currentFolderPath.value);
    };

    // 手动输入路径时实时更新
    const onManualPathInput = (event) => {
      manualDirectoryPath.value = event.target.value;
      if (manualDirectoryPath.value.trim()) {
        isUsingManualPath.value = true;
        selectedFolder.value = null; // 取消选中的文件夹
      }
    };

    // 使用手动输入的路径
    const useManualPath = () => {
      if (manualDirectoryPath.value.trim()) {
        console.log('使用手动输入路径:', manualDirectoryPath.value);
        isUsingManualPath.value = true;
        selectedFolder.value = null;
      } else {
        ElMessage.warning('请输入路径');
      }
    };

    // 确认文件夹选择
    const confirmFolderSelection = () => {
      let selectedPath = '';
      
      // 优先使用手动输入的路径
      if (isUsingManualPath.value && manualDirectoryPath.value.trim()) {
        selectedPath = manualDirectoryPath.value.trim();
      } else if (selectedFolder.value) {
        selectedPath = selectedFolder.value.Path;
      } else if (manualDirectoryPath.value.trim()) {
        selectedPath = manualDirectoryPath.value.trim();
      } else {
        ElMessage.warning('请先选择或输入目录路径');
        return;
      }

      console.log('确认选择路径:', selectedPath, '目标:', currentPathTarget.value);

      // 根据目标更新对应的表单字段
      if (currentPathTarget.value === 'cert') {
        webForm.value.defineCertPath = selectedPath;
        console.log('更新证书路径为:', webForm.value.defineCertPath);
        ElMessage.success(`证书路径已设置为: ${selectedPath}`);
      } else if (currentPathTarget.value === 'cache') {
        cacheForm.value.path = selectedPath;
        console.log('更新缓存路径为:', cacheForm.value.path);
        ElMessage.success(`缓存路径已设置为: ${selectedPath}`);
      }

      closeFolderModal();
    };

    // 关闭文件夹选择对话框
    const closeFolderModal = () => {
      console.log('关闭文件夹选择对话框');
      showFolderModal.value = false;
      selectedFolder.value = null;
      manualDirectoryPath.value = '';
      currentPathTarget.value = '';
      isUsingManualPath.value = false;
    };

    // 获取设置信息
    const fetchSettings = async () => {
      try {
        loading.value = true;
        const response = await fetch(`${API_BASE_URL}/get-settingInfo`, {
          credentials: 'include'
        });
        const data = await response.json();

        if (data.errCode === 1000) {
          ElMessage.warning('请先登录');
          router.push('/user-login?callback=sys-setting');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '获取设置信息失败');
        }

        // 更新Web设置
        webSettings.value = {
          httpPort: data.webport.toString(),
          httpStatus: data.webSwitch ? 'on' : 'off',
          httpsPort: data.httpsWebPort.toString(),
          httpsStatus: data.httpsWebSwitch ? 'on' : 'off',
          defineCertPath: data.defineCertPath,
          webdavIndex: data.webdavIndex
        };

        // 更新免密登录设置
        authSettings.value.status = data.loAuthLogin ? '已开启' : '已关闭';

        // 更新缓存设置
        cacheSettings.value = {
          path: data.tmpStorage,
          time: data.tmpStorageTime.toString(),
          recycle: data.recycleFlag ? 'on' : 'off'
        };

        // 更新安全路径
        securitySettings.value.path = data.securePath;

        // 更新日志设置
        logSettings.value.level = data.logData.level;
        logSettings.value.ouput = data.logData.ouput;
        logSettings.value.day = data.logData.day;
        logSettings.value.userLogDay = data.logData.userLogDay;

        currentPath.value = data.currentPath;

        if (data.loginPage) {
          loginPageSettings.value.loginPage = data.loginPage;
        }

      } catch (error) {
        console.error('获取设置信息失败:', error);
        ElMessage.error(error.message || '获取设置信息失败');
      } finally {
        loading.value = false;
      }
    };

    // 更新登录后页面配置
    const updateLoginPage = async () => {
      try {
        loginPageLoading.value = true;
        const params = new URLSearchParams();
        params.append('loginPage', loginPageForm.value.loginPage);
        
        const response = await fetch(`${API_BASE_URL}/update-login-page`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '更新登录后页面配置失败');
        }
        
        loginPageSettings.value.loginPage = loginPageForm.value.loginPage;
        loginPageDialogVisible.value = false;
        ElMessage.success('登录后页面配置已更新');
      } catch (error) {
        console.error('更新登录后页面配置失败:', error);
        ElMessage.error(error.message || '更新登录后页面配置失败');
      } finally {
        loginPageLoading.value = false;
      }
    };

    // 更新Web配置
    const updateWebConfig = async () => {
      try {
        webLoading.value = true;
        const params = new URLSearchParams();
        params.append('webPort', parseInt(webForm.value.httpPort));
        params.append('webSwitch', webForm.value.httpStatus);
        params.append('httpsWebPort', parseInt(webForm.value.httpsPort));
        params.append('httpsWebSwitch', webForm.value.httpsStatus);
        params.append('defineCertPath', webForm.value.defineCertPath);
        params.append('webdavIndex', webForm.value.webdavIndex);
        
        const response = await fetch(`${API_BASE_URL}/update-web-config`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '更新Web配置失败');
        }
        
        webSettings.value = { ...webForm.value };
        webDialogVisible.value = false;
        ElMessage.success('Web配置已更新');

        restartSoftware();
      } catch (error) {
        console.error('更新Web配置失败:', error);
        ElMessage.error(error.message || '更新Web配置失败');
      } finally {
        webLoading.value = false;
      }
    };

    // 切换免密登录
    const toggleAuthLogin = async () => {
      try {
        authLoading.value = true;

        const authStatus = authForm.value.status === '已开启';
        const url = authStatus
          ? `${API_BASE_URL}/start-loAuthLogin`
          : `${API_BASE_URL}/stop-loAuthLogin`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include'
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '更新免密登录设置失败');
        }

        authSettings.value.status = authStatus ? '已开启' : '已关闭';
        authDialogVisible.value = false;
        ElMessage.success(`免密登录已${authStatus ? '开启' : '关闭'}`);
      } catch (error) {
        console.error('更新免密登录设置失败:', error);
        ElMessage.error(error.message || '更新免密登录设置失败');
      } finally {
        authLoading.value = false;
      }
    };

    // 更新日志配置
    const updateLogConfig = async () => {
      try {
        logLoading.value = true;
        
        if (!logForm.value.level) {
          ElMessage.warning('请选择系统日志级别');
          return;
        }
        
        if (logForm.value.day < 0 || logForm.value.day > 30) {
          ElMessage.warning('系统日志天数必须在0-30之间');
          return;
        }
        
        if (logForm.value.userLogDay < 0 || logForm.value.userLogDay > 365) {
          ElMessage.warning('用户日志天数必须在0-365之间');
          return;
        }

        const params = new URLSearchParams();
        params.append('level', logForm.value.level);
        params.append('day', logForm.value.day);
        params.append('userlogday', logForm.value.userLogDay);
        
        const response = await fetch(`${API_BASE_URL}/update-logConfig`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '更新日志配置失败');
        }

        logSettings.value = {
          ...logSettings.value,
          level: logForm.value.level,
          day: logForm.value.day,
          userLogDay: logForm.value.userLogDay
        };
        logDialogVisible.value = false;
        ElMessage.success('日志配置已更新');
      } catch (error) {
        console.error('更新日志配置失败:', error);
        ElMessage.error(error.message || '更新日志配置失败');
      } finally {
        logLoading.value = false;
      }
    };

    // 更新缓存设置
    const updateCacheSettings = async () => {
      try {
        cacheLoading.value = true;
        const params = new URLSearchParams();
        params.append('tmpStorage', cacheForm.value.path);
        params.append('tmpStorageTime', parseInt(cacheForm.value.time));
        params.append('recycleFlag', cacheForm.value.recycle);
        
        const response = await fetch(`${API_BASE_URL}/update-tmpStorage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '更新缓存设置失败');
        }
        
        cacheSettings.value = { ...cacheForm.value };
        cacheDialogVisible.value = false;
        ElMessage.success('缓存设置已更新');
      } catch (error) {
        console.error('更新缓存设置失败:', error);
        ElMessage.error(error.message || '更新缓存设置失败');
      } finally {
        cacheLoading.value = false;
      }
    };

    // 重置缓存设置
    const resetCacheSettings = async () => {
      try {
        cacheLoading.value = true;
        const params = new URLSearchParams();
        params.append('tmpStorage', '000');
        params.append('tmpStorageTime', '000');
        params.append('recycleFlag', cacheForm.value.recycle);
        
        const response = await fetch(`${API_BASE_URL}/update-tmpStorage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '重置缓存设置失败');
        }
        
        await fetchSettings();
        cacheForm.value = { ...cacheSettings.value };
        ElMessage.success('已恢复默认缓存设置');
      } catch (error) {
        console.error('重置缓存设置失败:', error);
        ElMessage.error(error.message || '重置缓存设置失败');
      } finally {
        cacheLoading.value = false;
      }
    };

    // 更新安全路径
    const updateSecurityPath = async () => {
      try {
        securityLoading.value = true;
        const params = new URLSearchParams();
        params.append('securePath', securityForm.value.path);
        
        const response = await fetch(`${API_BASE_URL}/update-securePath`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '更新安全路径失败');
        }

        securitySettings.value.path = securityForm.value.path;
        securityDialogVisible.value = false;

        // 执行登出
        await fetch(`${API_BASE_URL}/logout`, {
          method: 'POST',
          credentials: 'include',
        });
        
        // 清除用户信息
        localStorage.removeItem('userInfo');
        
        // 跳转到登录页
        router.push('/user-login?callback=sys-setting');
        
        ElMessage.success('安全路径已更新');
      } catch (error) {
        console.error('更新安全路径失败:', error);
        ElMessage.error(error.message || '更新安全路径失败');
      } finally {
        securityLoading.value = false;
      }
    };

    // 备份所有配置
    const backupAllConfig = async () => {
      let url = HOME_BASE_URL + '/download-config-file';
      window.open(url);
    };

    // 备份cfg配置
    const backupCfgConfig = async () => {
      let url = HOME_BASE_URL + '/download-cfg-config-file';
      window.open(url);
    };

    // 重启软件
    const restartSoftware = async () => {
      try {
        await ElMessageBox.confirm('确定要重启软件吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        
        restartLoading.value = true;
        const response = await fetch(`${API_BASE_URL}/restart-proccess`, {
          credentials: 'include'
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '重启软件失败');
        }
        
        ElMessage.success('软件将在3秒后重启');
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          console.error('重启软件失败:', error);
          ElMessage.error('当前连接断开，请刷新页面:' + error.message);
        }
      } finally {
        restartLoading.value = false;
      }
    };

    // 自动刷新
    const startAutoRefresh = () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
      refreshTimer = setInterval(() => {
        fetchSettings();
      }, 10000);
    };

    onMounted(() => {
      fetchSettings();
      checkSysStatus();
      startAutoRefresh();
    });

    onUnmounted(() => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
      stopSysLogAutoRefresh();
    });

    return {
      isMobile,
      loadCompo,
      webSettings,
      authSettings,
      authLoading,
      cacheSettings,
      securitySettings,
      loginPageSettings,
      loginPageLoading,
      webLoading,
      cacheLoading,
      securityLoading,
      backupLoading,
      uploadLoading,
      restartLoading,
      logSettings,
      logLoading,
      currentPath,

      // 对话框状态
      loginPageDialogVisible,
      webDialogVisible,
      authDialogVisible,
      logDialogVisible,
      cacheDialogVisible,
      securityDialogVisible,

      // 系统日志相关
      sysLogDialogVisible,
      sysLogLoading,
      sysLogClearing,
      sysLogDownloading,
      sysLogsData,
      sysLogTotal,
      sysLogCurrentPage,
      sysLogPageSize,
      sysLogContentRef,
      sysAutoRefreshEnabled,

      // 文件夹选择相关
      showFolderModal,
      currentFolderPath,
      folders,
      selectedFolder,
      manualDirectoryPath,

      // 表单数据
      loginPageForm,
      webForm,
      authForm,
      logForm,
      cacheForm,
      securityForm,

      // 图标
      Refresh,
      Download,
      InfoFilled,

      // 方法
      getPageName,
      openLoginPageDialog,
      openWebDialog,
      openAuthDialog,
      openLogDialog,
      openCacheDialog,
      openSecurityDialog,
      updateLoginPage,
      updateWebConfig,
      toggleAuthLogin,
      updateLogConfig,
      updateCacheSettings,
      resetCacheSettings,
      updateSecurityPath,
      backupAllConfig,
      backupCfgConfig,
      restartSoftware,
      
      // 系统日志方法
      showSysLogDialog,
      toggleSysAutoRefresh,
      refreshSysLogs,
      clearSysLogs,
      downloadAllSysLogs,
      handleSysLogSizeChange,
      handleSysLogPageChange,
      
      // 文件夹选择方法
      openFolderSelector,
      selectFolder,
      enterFolder,
      goToParentFolder,
      confirmFolderSelection,
      closeFolderModal,
      onManualPathInput,
      useManualPath
    };
  }
};
</script>

<style scoped>
.settings-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 40px);
}

.container {
  margin: 0 auto;
}

.setting-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
}

.card-header {
  background-color: #f0f2f5;
  padding: 16px 20px;
  border-bottom: 1px solid #e6e6e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h2 {
  margin: 0;
  font-size: 16px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-header i {
  font-size: 18px;
}

.card-actions {
  display: flex;
  gap: 8px;
}

.card-content {
  padding: 20px;
}

.backup-actions {
  display: flex;
  gap: 10px;
}

:deep(.el-descriptions) {
  margin-top: 0;
}

:deep(.el-descriptions__label) {
  width: 150px;
  font-weight: 500;
  background-color: #fafafa;
}

/* 路径输入组样式 */
.path-input-group {
  display: flex;
  gap: 8px;
  width: 100%;
}

.path-input-group .el-input {
  flex: 1;
}

/* 文件夹选择模态框样式 - 确保在最上层 */
.folder-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.folder-modal-container {
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  animation: modalFadeIn 0.3s ease;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.folder-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow: hidden;
}

.folder-modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 1;
}

.folder-modal-header h3 {
  margin: 0;
  font-size: 14px;
  color: #333;
  flex: 1;
  word-break: break-all;
}

.nav-btn {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  white-space: nowrap;
}

.nav-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  padding: 0 8px;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.manual-input-section {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: #fff;
}

.input-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.input-controls {
  display: flex;
  gap: 8px;
}

.directory-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s;
}

.directory-input:focus {
  border-color: #1890ff;
}

.use-path-btn {
  padding: 8px 16px;
  background: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
  transition: background 0.2s;
}

.use-path-btn:hover {
  background: #40a9ff;
}

.input-hint {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
}

.folder-modal-content {
  padding: 0;
  overflow-y: auto;
  flex: 1;
  min-height: 200px;
  max-height: 300px;
}

.directory-list {
  padding: 8px 0;
}

.directory-item {
  padding: 10px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background 0.2s;
  border-bottom: 1px solid #f5f5f5;
}

.directory-item:hover {
  background: #f5f5f5;
}

.directory-item.selected {
  background: #e6f7ff;
}

.folder-icon {
  font-size: 18px;
  color: #ffc53d;
}

.directory-name {
  flex: 1;
  font-size: 13px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.enter-btn {
  background: none;
  border: none;
  font-size: 16px;
  color: #999;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  opacity: 0;
  transition: all 0.2s;
}

.directory-item:hover .enter-btn {
  opacity: 1;
}

.enter-btn:hover {
  background: #e6f7ff;
  color: #1890ff;
}

.empty-directory {
  text-align: center;
  color: #999;
  padding: 40px 20px;
  font-size: 14px;
}

.folder-modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #eee;
  background: #f8f9fa;
}

.selected-path {
  font-size: 12px;
  color: #666;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: white;
  border-radius: 4px;
  word-break: break-all;
  border: 1px solid #eee;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.cancel-btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.cancel-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.confirm-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background: #1890ff;
  color: white;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  min-width: 80px;
}

.confirm-btn:hover {
  background: #40a9ff;
}

.confirm-btn:disabled {
  background: #bae0ff;
  cursor: not-allowed;
}

/* 日志对话框样式 */
.log-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.log-stats {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 13px;
  color: #606266;
}

.auto-refresh-control {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 16px;
  border-left: 1px solid #dcdfe6;
}

.refresh-label {
  font-size: 12px;
  color: #909399;
}

.refresh-interval {
  font-size: 12px;
  color: #67c23a;
}

.log-actions {
  display: flex;
  gap: 8px;
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.empty-log {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
}

.empty-log .el-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.empty-log p {
  margin: 0;
  font-size: 14px;
}

.log-list {
  padding: 12px;
}

.log-item {
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.log-item:last-child {
  border-bottom: none;
}

.log-item.log-level-error {
  background-color: #fef0f0;
}

.log-item.log-level-warn {
  background-color: #fdf6ec;
}

.log-time {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-level {
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-level.level-error {
  color: #f56c6c;
}

.log-level.level-warn {
  color: #e6a23c;
}

.log-level.level-info {
  color: #409eff;
}

.log-message {
  color: #303133;
  word-break: break-all;
  flex: 1;
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .card-content {
    padding: 15px;
  }
  
  .backup-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .folder-modal-container {
    width: 95%;
    max-height: 90vh;
  }
  
  .folder-modal {
    max-height: 90vh;
  }
  
  .folder-modal-header {
    flex-wrap: wrap;
  }
  
  .folder-modal-header h3 {
    font-size: 13px;
    order: 3;
    width: 100%;
    margin-top: 8px;
  }
  
  .input-controls {
    flex-direction: column;
  }
  
  .use-path-btn {
    width: 100%;
  }
  
  .directory-item {
    padding: 12px 16px;
  }
  
  .enter-btn {
    opacity: 1;
    padding: 8px 12px;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .cancel-btn,
  .confirm-btn {
    width: 100%;
    padding: 12px;
  }
  
  .log-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .log-stats {
    width: 100%;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .auto-refresh-control {
    padding-left: 0;
    border-left: none;
  }
  
  .log-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  .log-item {
    font-size: 12px;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .log-time {
    width: 100%;
    margin-bottom: 2px;
  }
  
  .log-message {
    width: 100%;
    margin-left: 0;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>